import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, FileSearch, Home, Euro, Hammer, Wrench, Key, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const EtapesConstructionPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div
        className="relative h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1600)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-white">
            <div className="flex items-center text-sm mb-4">
              <Link to="/" className="opacity-90 hover:opacity-100">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span>Étapes de construction</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold">Les étapes de construction de votre maison</h1>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-gray-600 mb-4">
            Construire sa maison est un projet majeur qui demande anticipation et organisation. Il est essentiel de bien comprendre <strong>les étapes de construction de maison</strong> pour savoir à quoi s'attendre à chaque phase. Depuis l'étude de faisabilité jusqu'à la remise des clés, chaque étape doit être réalisée avec rigueur pour garantir la <strong>qualité</strong> et la <strong>durabilité</strong> de votre habitation.
          </p>
          <p className="text-lg text-gray-600">
            Chez <Link to="/a-propos" className="text-[#c4d600] hover:underline">MRTP SARL</Link>, nous vous accompagnons tout au long de ce processus, en leur apportant <strong>expertise</strong>, <strong>conseils</strong> et <strong>transparence</strong>. Que vous soyez à la recherche de votre premier logement ou que vous souhaitiez faire construire une maison sur mesure, notre méthode vous permet de concrétiser votre projet en toute sérénité. Découvrons ensemble les différentes phases de votre projet de construction de maison individuelle.
          </p>
        </div>
      </div>

      {/* Étape 1: Étude et conception */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#c4d600] rounded-full flex items-center justify-center text-gray-800 font-bold text-xl mr-4">
                  1
                </div>
                <div>
                  <p className="text-[#c4d600] font-semibold">L'étude et</p>
                  <h2 className="text-3xl font-bold text-gray-800">LA CONCEPTION DU PROJET</h2>
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-gray-800 mb-4 mt-6">
                Avant de lancer la construction, il est essentiel de bien préparer le projet en amont. Cette étape comprend plusieurs points essentiels :
              </h3>
              
              <p className="text-gray-600 mb-4">
                Tout d'abord, le <Link to="/nos-terrains" className="text-[#c4d600] hover:underline">choix du terrain</Link> est une décision primordiale. Il doit être adapté à votre projet, à votre budget et à vos attentes en matière d'implantation. Il est donc nécessaire de prendre en compte la nature du sol, l'exposition, l'accessibilité et la proximité des commodités. Une étude de sol peut être réalisée afin d'anticiper d'éventuelles contraintes techniques liées à la nature du terrain.
              </p>
              
              <p className="text-gray-600 mb-4">
                Une fois le terrain trouvé, la <strong>conception des plans</strong> peut débuter. Cette phase permet en effet de définir l'agencement intérieur et extérieur de votre future maison. Chez MRTP SARL, nous réalisons une étude approfondie afin de proposer une maison <strong>fonctionnelle</strong>, <strong>esthétique</strong> et <strong>conforme aux normes</strong> en vigueur.
              </p>
              
              <p className="text-gray-600">
                Nous étudions également les <strong>performances énergétiques</strong> pour optimiser l'isolation et la consommation énergétique. Une maison bien conçue vous offrira alors un <strong>confort optimal</strong> tout en réduisant vos factures d'énergie sur le long terme.
              </p>
            </div>
            
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800"
                alt="Plans et conception"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Étape 2: Financement et démarches */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800"
                alt="Financement"
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#c4d600] rounded-full flex items-center justify-center text-gray-800 font-bold text-xl mr-4">
                  2
                </div>
                <div>
                  <p className="text-[#c4d600] font-semibold">LE FINANCEMENT ET</p>
                  <h2 className="text-3xl font-bold text-gray-800">LES DEMARCHES ADMINISTRATIVES</h2>
                </div>
              </div>
              
              <p className="text-gray-600 mb-4">
                Le financement est un élément clé du projet. Il est donc important d' <strong>évaluer votre budget</strong> en fonction de vos revenus, de votre apport personnel et des aides financières disponibles. Nous vous accompagnons dans cette étape en vous aidant à identifier les solutions de financement les plus adaptées à votre projet.
              </p>
              
              <p className="text-gray-600 mb-4">
                Une fois le financement validé, place aux démarches administratives. Le <strong>permis de construire</strong> est une étape obligatoire. Il doit en effet être conforme aux règles d'urbanisme en vigueur dans votre commune. Nous nous chargeons de la constitution du dossier et du dépôt auprès des autorités compétentes.
              </p>
              
              <p className="text-gray-600">
                Grâce au <strong>CCMI</strong>, vous bénéficiez des <strong>garanties indispensables</strong>, telles que la <strong>garantie de parfait achèvement, la garantie biennale et la garantie décennale, la garantie de livraison</strong>, qui assurent alors la pérennité de votre maison après la livraison.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Étape 3: Préparation du terrain */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-[#c4d600] rounded-full flex items-center justify-center text-gray-800 font-bold text-xl mr-4">
                3
              </div>
              <div>
                <p className="text-[#c4d600] font-semibold">LA PREPARATION DU TERRAIN ET</p>
                <h2 className="text-3xl font-bold text-gray-800">LE DEBUT DES TRAVAUX</h2>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <Hammer className="w-12 h-12 text-[#c4d600] mb-4" />
              <h4 className="font-bold text-gray-800 mb-2">Le terrassement</h4>
              <p className="text-sm text-gray-600">du terrain, qui permet en effet d'adapter la surface pour accueillir la future maison</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <Wrench className="w-12 h-12 text-[#c4d600] mb-4" />
              <h4 className="font-bold text-gray-800 mb-2">La viabilisation</h4>
              <p className="text-sm text-gray-600">qui consiste donc à raccorder le terrain aux différents réseaux (eau, électricité, assainissement, télécommunications)</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <Home className="w-12 h-12 text-[#c4d600] mb-4" />
              <h4 className="font-bold text-gray-800 mb-2">L'implantation</h4>
              <p className="text-sm text-gray-600">de la maison, qui assure alors le bon positionnement de la construction par rapport aux plans validés</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <CheckCircle className="w-12 h-12 text-[#c4d600] mb-4" />
              <h4 className="font-bold text-gray-800 mb-2">Les fondations</h4>
              <p className="text-sm text-gray-600">qui garantissent en effet la solidité et la pérennité de votre maison en fonction des caractéristiques du sol</p>
            </div>
          </div>
        </div>
      </div>

      {/* Étape 4: Second œuvre */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-[#c4d600] rounded-full flex items-center justify-center text-gray-800 font-bold text-xl mr-4">
                4
              </div>
              <div>
                <p className="text-[#c4d600] font-semibold">LE CONFORT INTERIEUR AVEC</p>
                <h2 className="text-3xl font-bold text-gray-800">LE SECOND OEUVRE</h2>
              </div>
            </div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Une fois le gros œuvre terminé, place aux <strong>travaux du second œuvre</strong>, qui apportent confort et fonctionnalité à votre maison. Isolation, cloisons et équipements intérieurs prennent forme pour créer un espace agréable à vivre.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#c4d600] rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="w-10 h-10 text-gray-800" />
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Isolation</h4>
              <p className="text-sm text-gray-600">L'isolation thermique et acoustique, essentielle pour valoriser les économies d'énergies et assurer votre confort de vie au quotidien.</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-[#c4d600] rounded-full flex items-center justify-center mx-auto mb-4">
                <Wrench className="w-10 h-10 text-gray-800" />
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Cloisons</h4>
              <p className="text-sm text-gray-600">La pose des cloisons intérieures pour délimiter les limites et surfaces de chaque pièces.</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-[#c4d600] rounded-full flex items-center justify-center mx-auto mb-4">
                <Hammer className="w-10 h-10 text-gray-800" />
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Équipements</h4>
              <p className="text-sm text-gray-600">L'installation des menuiseries (portes, fenêtres) et des équipements de plomberie et d'électricité.</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-[#c4d600] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-gray-800" />
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Finitions</h4>
              <p className="text-sm text-gray-600">Les finitions apportent la touche finale à votre maison selon vos envies et vos préférences.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Étape 5: Réception et remise des clés */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#c4d600] rounded-full flex items-center justify-center text-gray-800 font-bold text-xl mr-4">
                  5
                </div>
                <div>
                  <p className="text-[#c4d600] font-semibold">LA RECEPTION DES TRAVAUX ET</p>
                  <h2 className="text-3xl font-bold text-gray-800">LA REMISE DES CLÉS</h2>
                </div>
              </div>
              
              <p className="text-lg text-gray-600 mb-6 mt-6">
                La <strong>dernière étape</strong> est alors la livraison de votre maison. Une visite détaillée est réalisée pour vérifier la conformité des travaux et identifier d'éventuelles corrections à apporter. Une fois validée, <strong>la remise des clés</strong> marque en effet le début de votre nouvelle vie dans votre maison neuve.
              </p>
              
              <div className="bg-white rounded-lg p-6 shadow-md">
                <Key className="w-12 h-12 text-[#c4d600] mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-3">Votre nouvelle vie commence ici</h3>
                <p className="text-gray-600">
                  Profitez de votre maison neuve avec toutes les garanties nécessaires pour votre tranquillité d'esprit.
                </p>
              </div>
            </div>
            
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800"
                alt="Remise des clés"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#c4d600] font-semibold mb-2">POURQUOI CHOISIR</p>
          <h2 className="text-4xl font-bold text-gray-800 mb-6">LA MAISON DES COMPAGNONS ?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Construire une maison est en effet un projet d'envergure qui nécessite une expertise fiable et un accompagnement personnalisé. La MRTP SARL vous assure un suivi rigoureux, un respect des délais et une qualité de construction optimale pour les étapes de construction de maison.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button asChild className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold">
              <Link to="/modeles">Découvrez nos modèles de maisons</Link>
            </Button>
            <Button asChild variant="outline" className="border-[#c4d600] text-[#c4d600] hover:bg-[#c4d600] hover:text-gray-800 font-semibold">
              <Link to="/maisons-sur-mesure">Nos maisons sur mesure</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* CTA to previous page */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Retrouvez les détails de notre accompagnement dédié</h3>
          <Button asChild className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold text-lg px-8 py-6">
            <Link to="/projet-construction">Je clique pour être accompagné</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EtapesConstructionPage;